import React, {memo, type ComponentProps, type CSSProperties, useMemo, useEffect} from 'react';
import {useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import tinycolor from 'tinycolor2';
import classNames from 'classnames';

import {type UserProfile} from 'mattermost-redux/types/users';
import {getUser, getUser as selectUser, makeDisplayNameGetter} from 'mattermost-redux/selectors/entities/users';
import {getTheme} from 'mattermost-redux/selectors/entities/preferences';

import {type GlobalState} from 'types/store';

import {t} from 'utils/i18n';
import {getDisplayNameByUser, imageURLForUser} from 'utils/utils';

import SimpleTooltip, {useSynchronizedImmediate} from 'components/widgets/simple_tooltip';
import Avatar from 'components/widgets/users/avatar';
import ProfilePopover from 'components/profile_popover';
import OverlayTrigger from 'components/overlay_trigger';

import './avatars.scss';
import {fetchUsersByIdsDebounced} from 'features/users/actions/fetch_users_by_ids';
import {useAppDispatch, useAppSelector} from 'stores/redux_store';

type Props = {
    userIds: Array<UserProfile['id']>;
    totalUsers?: number;
    breakAt?: number;
    size?: ComponentProps<typeof Avatar>['size'];
    interactive?: boolean;
    fetchMissingUsers?: boolean;
};

const OTHERS_DISPLAY_LIMIT = 99;

function countMeta<T>(
    items: T[],
    total = items.length,
): [T[], T[], {overflowUnnamedCount: number; nonDisplayCount: number}] {
    const breakAt = Math.max(items.length, total) > 4 ? 3 : 4;

    const displayItems = items.slice(0, breakAt);
    const overflowItems = items.slice(breakAt);

    const overflowUnnamedCount = Math.max(total - displayItems.length - overflowItems.length, 0);
    const nonDisplayCount = overflowItems.length + overflowUnnamedCount;

    return [displayItems, overflowItems, {overflowUnnamedCount, nonDisplayCount}];
}

const displayNameGetter = makeDisplayNameGetter();

function UserAvatar({
    userId,
    overlayProps,
    interactive,
    ...props
}: {
    userId: UserProfile['id'];
    overlayProps: Partial<ComponentProps<typeof SimpleTooltip>>;
    interactive: boolean;
} & ComponentProps<typeof Avatar>) {
    const name = useSelector((state: GlobalState) => {
        const user = selectUser(state, userId);
        return displayNameGetter(state, true)(user);
    });
    const lastPicUpdate = useAppSelector((state) => getUser(state, userId)?.last_picture_update || 0);
    const userDisplayName = useSelector((state: GlobalState) => {
        const user = selectUser(state, userId);
        return getDisplayNameByUser(state, user);
    });
    const url = imageURLForUser(userId, lastPicUpdate);

    const profilePictureURL = userId ? imageURLForUser(userId, lastPicUpdate) : '';

    if (!interactive) {
        return (
            <Avatar
                url={url}
                username={userDisplayName}
                {...props}
            />
        );
    }

    return (
        <OverlayTrigger
            trigger='click'
            placement='top'
            rootClose={true}
            overlay={
                <ProfilePopover
                    userId={userId}
                    src={profilePictureURL}
                />
            }
        >
            <SimpleTooltip
                id={`name-${userId}`}
                content={name}
                {...overlayProps}
            >
                <button
                    className={'style--none'}
                    tabIndex={-1}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Avatar
                        url={url}
                        username={userDisplayName}
                        {...props}
                    />
                </button>
            </SimpleTooltip>
        </OverlayTrigger>
    );
}

function Avatars({
    size,
    userIds,
    totalUsers,
    interactive = false,
    fetchMissingUsers = true,
}: Props) {
    const {formatMessage} = useIntl();
    const dispatch = useAppDispatch();
    const [overlayProps, setImmediate] = useSynchronizedImmediate();
    const [displayUserIds, overflowUserIds, {overflowUnnamedCount, nonDisplayCount}] = countMeta(userIds, totalUsers);
    const overflowNames = useSelector((state: GlobalState) => {
        return overflowUserIds.map((userId) => displayNameGetter(state, true)(selectUser(state, userId))).join(', ');
    });

    const {centerChannelBg, centerChannelColor} = useSelector(getTheme);
    const avatarStyle: CSSProperties = useMemo(() => ({
        background: tinycolor.mix(centerChannelBg, centerChannelColor, 8).toRgbString(),
    }), [centerChannelBg, centerChannelColor]);

    useEffect(() => {
        if (fetchMissingUsers) {
            dispatch(fetchUsersByIdsDebounced(userIds));
        }
    }, [fetchMissingUsers, userIds, dispatch]);

    const avatars: React.ReactNode[] = [];

    displayUserIds.forEach((id) => {
        avatars.push(
            <UserAvatar
                interactive={interactive}
                style={avatarStyle}
                key={id}
                userId={id}
                size={size}
                tabIndex={0}
                overlayProps={overlayProps}
            />,
        );
    });

    if (nonDisplayCount) {
        const lastAvatar = (
            <Avatar
                key='last'
                style={avatarStyle}
                size={size}
                tabIndex={0}
                text={nonDisplayCount > OTHERS_DISPLAY_LIMIT ? `${OTHERS_DISPLAY_LIMIT}+` : `+${nonDisplayCount}`}
            />
        );
        if (interactive) {
            avatars.push(
                <SimpleTooltip
                    key='last'
                    id={'names-overflow'}
                    {...overlayProps}
                    content={overflowUserIds.length ? formatMessage(
                        {
                            id: t('avatars.overflowUsers'),
                            defaultMessage: '{overflowUnnamedCount, plural, =0 {{names}} =1 {{names} and one other} other {{names} and # others}}',
                        },
                        {
                            overflowUnnamedCount,
                            names: overflowNames,
                        },
                    ) : formatMessage(
                        {
                            id: t('avatars.overflowUnnamedOnly'),
                            defaultMessage: '{overflowUnnamedCount, plural, =1 {one other} other {# others}}',
                        },
                        {overflowUnnamedCount},
                    )}
                >
                    {lastAvatar}
                </SimpleTooltip>,
            );
        } else {
            avatars.push(lastAvatar);
        }
    }

    return (
        <div
            className={classNames(`Avatars Avatars___${size}`, {interactive})}
            onMouseLeave={() => setImmediate(false)}
        >
            {avatars}
        </div>
    );
}

export default memo(Avatars);
